import {useState, useCallback, forwardRef} from "react";
import {Col, css, Row, Text} from "@cdx/ds";
import {RevealContainer} from "../hooks/useReveal";
import {RawSpinner} from "../xui/Spinner";
import cx from "../cx";
import {labelStyles as styles} from "./form.css";

/**
 * @deprecated Legacy field label. Use DSField / DSFieldLabel from @cdx/ds, or a forms-next field.
 * @type {any}
 */
export const Label = ({showingErrors, className, ...rest}) => (
  <label
    className={css(
      {
        color: showingErrors ? "error" : "secondary",
        textType: "label11Caps",
        display: "block",
        position: "relative",
        alignSelf: "start",
      },
      className
    )}
    {...rest}
  />
);

const InputRow = ({showingErrors, isFocussed, className, ...rest}) => (
  <Row
    sp="12px"
    align="baseline"
    className={cx(
      styles.row.base,
      showingErrors && styles.row.error,
      isFocussed && styles.row.focus,
      className
    )}
    {...rest}
  />
);

const useFocussedState = ({onBlur: onParentBlur, onFocus: onParentFocus}) => {
  const [isFocussed, setIsFocussed] = useState(false);
  return [
    isFocussed,
    useCallback(
      (e) => {
        if (onParentFocus) onParentFocus(e);
        setIsFocussed(true);
      },
      [onParentFocus]
    ),
    useCallback(
      (e) => {
        if (onParentBlur) onParentBlur(e);
        setIsFocussed(false);
      },
      [onParentBlur]
    ),
  ];
};

/** @deprecated Legacy field label. Use DSField / DSFieldLabel from @cdx/ds, or a forms-next field. */
export const SimpleWithLabel = ({label, className, htmlFor, error, children}) => (
  <Col sp="12px" className={className}>
    <Label htmlFor={htmlFor}>{label}</Label>
    <Col sp="8px">
      {children}
      {error && (
        <Text type="label12" allowWrap color="error">
          {error}
        </Text>
      )}
    </Col>
  </Col>
);

/**
 * @deprecated Legacy field label. Use DSField / DSFieldLabel from @cdx/ds, or a forms-next field.
 * @type {any}
 */
export const WithLabel = ({
  children,
  className,
  style,
  label,
  name,
  showErrors = [],
  hint,
  hasPendingValidation,
}) => (
  <Col
    sp="12px"
    className={className}
    style={style}
    data-cdx-context={typeof label === "string" ? label : null}
  >
    {label && (
      <Label htmlFor={name} showingErrors={showErrors.length > 0}>
        {label}
        <RevealContainer show={hasPendingValidation}>
          <RawSpinner />
        </RevealContainer>
      </Label>
    )}
    <Col sp="8px">
      {children}
      {showErrors.length > 0 && (
        <Row sp="8px" wrap>
          {showErrors.map((e, i) => (
            <Text type="label12" allowWrap color="error" key={i}>
              {e}
            </Text>
          ))}
        </Row>
      )}
      {hint && (
        <Text type="label12light" allowWrap color="secondary">
          {hint}
        </Text>
      )}
    </Col>
  </Col>
);

/** @deprecated Legacy field label. Use DSField / DSFieldLabel from @cdx/ds, or a forms-next field. */
export const FieldWithoutLabel = forwardRef((props, ref) => {
  const {
    children,
    showErrors = [],
    onFocus,
    onBlur,
    as: Comp,
    prefix,
    postfix,
    className,
    hasPendingValidation,
    fieldClassName,
    ...rest
  } = props;
  const [isFocussed, onWrappedFocus, onWrappedBlur] = useFocussedState({onFocus, onBlur});
  const showingErrors = showErrors.length > 0;

  // TODO: show errors

  return (
    <InputRow isFocussed={isFocussed} showingErrors={showingErrors} className={className}>
      {prefix}
      <Comp
        showingErrors={showingErrors}
        ref={ref}
        onFocus={onWrappedFocus}
        onBlur={onWrappedBlur}
        className={fieldClassName}
        {...rest}
      />
      {postfix}
    </InputRow>
  );
});

/**
 * @deprecated Legacy field label. Use DSField / DSFieldLabel from @cdx/ds, or a forms-next field.
 * @type {any}
 */
export const FieldWithLabel = forwardRef((props, ref) => {
  const {
    as: Comp,
    name,
    label = name,
    showErrors = [],
    prefix,
    postfix,
    onFocus,
    onBlur,
    hint,
    className,
    fieldClassName,
    hasPendingValidation,
    style,
    fieldStyle,
    ...rest
  } = props;
  const showingErrors = showErrors.length > 0;
  const [isFocussed, onWrappedFocus, onWrappedBlur] = useFocussedState({onFocus, onBlur});

  return (
    <WithLabel
      label={label}
      name={label}
      showErrors={showErrors}
      hint={hint}
      className={className}
      style={style}
      hasPendingValidation={hasPendingValidation}
    >
      <InputRow isFocussed={isFocussed} showingErrors={showingErrors}>
        {prefix}
        <Comp
          showingErrors={showingErrors}
          id={name}
          name={name}
          ref={ref}
          onFocus={onWrappedFocus}
          onBlur={onWrappedBlur}
          style={fieldStyle}
          className={fieldClassName}
          {...rest}
        />
        {postfix}
      </InputRow>
    </WithLabel>
  );
});
